import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Callout,
  Confirmation,
  FormSection,
  Icon,
  ModalHead,
  Panel,
  Pill,
  Paragraph,
  ProgressBar,
  Stepper,
  StepperStep,
  Title,
  Tabs,
  Wizard,
  WizardFooter,
  WizardPage,
  toastError,
} from 'admination-react'
import { AutosaveStatus } from '@/core/components/form/AutosaveBoundary'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import { LanguageSwitcher } from '@/core/components/i18n/language-switcher'
import { currentLanguage, setLanguage, type Language } from '@/core/i18n'
import {
  PortalError,
  PortalTimeout,
  portalErrorDetail,
  abschliessen,
  currentPortalSession,
  getBlatt,
  pngBase64,
  saveBlatt,
  setSprache,
  signBlatt,
  type PortalBlatt,
  type PortalSessionId,
} from '../../api/portal'
import {
  BLATT_IDS,
  BLATT_TOTAL,
  SHEETS,
  blattKey,
  datenToSave,
  fehlendAuf,
  hasQuestions,
  isBlattComplete,
  isBlattId,
  readyToSign,
  requiredFilled,
  reviewValue,
  type BlattId,
  type BlattStatus,
  type Daten,
  type Fehlend,
} from '../../lib/teilnehmerakte'
import type { PortalPhase } from './portal-machine'
import type { LoadedSession } from './session'
import { SheetDocument, SheetView, readingInhalt } from './sheet-view'
import { retainedAutosave, useAutosave } from './use-autosave'
import { useSheetPdf } from './use-sheet-pdf'

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000

export type WizardExit = 'later' | 'idle' | 'finished' | 'ended'

export type TeilnehmerakteWizardProps = {
  autosaveIdentity: string
  autosaveLabel?: string
  personHeader?: { name: string; onOpen: () => void }
  phase: PortalPhase
  onNavigate: (phase: PortalPhase) => void
  loaded: LoadedSession
  sessionId: PortalSessionId
  onExit: (reason: WizardExit, pendingSave: Promise<boolean>) => void
  abgeschlossen?: boolean
}

type Notice = 'signUnclear' | null

type Closing = 'idle' | 'busy' | 'failed' | 'missing' | 'unclear'

function sessionGone(error: unknown): boolean {
  return error instanceof PortalError && error.status === 404
}

type SheetState = Partial<Omit<PortalBlatt, 'status'>> & { status: BlattStatus; loadedIn?: string; signatureSrc?: string }

type SheetStep = 'form' | 'document' | 'saved'

function initialStep(id: BlattId, status: BlattStatus): SheetStep {
  const sheet = SHEETS[id]
  if (sheet.kind !== 'sign') return 'form'
  return status !== 'unterschrieben' && hasQuestions(sheet) ? 'form' : 'document'
}

export function TeilnehmerakteWizard({
  autosaveIdentity,
  autosaveLabel,
  personHeader,
  phase,
  onNavigate,
  loaded,
  sessionId,
  onExit,
  abgeschlossen = false,
}: TeilnehmerakteWizardProps) {
  const { t } = useTranslation('portal')
  const language = currentLanguage()
  const [sheets, setSheets] = useState<Record<BlattId, SheetState>>(() => {
    const recovery = retainedAutosave(autosaveIdentity)
    return Object.fromEntries(BLATT_IDS.map((id) => {
      const sheet = loaded.blaetter[id]
      return [id, {
        ...sheet, status: loaded.statuses[id],
        daten: sheet ? loaded.statuses[id] === 'unterschrieben' ? sheet.daten : recovery[id] ?? loaded.retained?.[id] ?? sheet.daten : undefined,
        loadedIn: sheet ? language : undefined,
      }]
    })) as Record<BlattId, SheetState>
  })
  const sheetsRef = useRef(sheets)
  const updateSheets = useCallback((updater: (s: Record<BlattId, SheetState>) => Record<BlattId, SheetState>) => {
    const next = updater(sheetsRef.current)
    sheetsRef.current = next
    setSheets(next)
  }, [])
  const [loadError, setLoadError] = useState(false)
  const [signing, setSigning] = useState(false)
  const [notice, setNotice] = useState<Notice>(null)
  const [closing, setClosing] = useState<Closing>('idle')
  const [saveBlocked, setSaveBlocked] = useState(false)
  const [blockedBy, setBlockedBy] = useState<BlattId | null>(null)
  const [focusMissing, setFocusMissing] = useState(0)
  const [view, setView] = useState<{ id: BlattId; step: SheetStep } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const exiting = useRef(false)
  const finishTimedOut = useRef(false)
  const finishing = useRef(false)
  const signTimedOut = useRef(new Set<BlattId>())
  const endedRef = useRef<() => void>(() => undefined)

  const save = useCallback(async (id: BlattId, daten: Record<string, unknown>) => {
    let result: Awaited<ReturnType<typeof saveBlatt>>
    try {
      result = await saveBlatt(id, datenToSave(SHEETS[id], daten), sessionId)
    } catch (error) {
      if (sessionGone(error)) endedRef.current()
      throw error
    }
    if (result === 'unterschrieben') {
      updateSheets((s) => ({ ...s, [id]: { ...s[id], status: 'unterschrieben', daten: undefined, loadedIn: undefined } }))
      throw new Error(`Blatt ${id} ist bereits unterschrieben.`)
    }
    updateSheets((s) => (s[id].status === 'offen' ? { ...s, [id]: { ...s[id], status: 'gespeichert' } } : s))
  }, [sessionId, updateSheets])
  const autosave = useAutosave<BlattId>(save, autosaveIdentity, (id) => sheetsRef.current[id].status === 'unterschrieben', () => {
    toastError(`${autosaveLabel ?? "Teilnehmerakte"}: Die Änderungen konnten nicht gespeichert werden. Öffnen Sie die Akte erneut und versuchen Sie es noch einmal.`)
  })
  const recoverAutosave = autosave.recovery

  const complete = (id: BlattId) => isBlattComplete(SHEETS[id], sheets[id].status, sheets[id].daten)
  const allComplete = BLATT_IDS.every(complete)
  const doneCount = BLATT_IDS.filter(complete).length
  const firstOpen = (() => {
    const i = BLATT_IDS.findIndex((id) => !complete(id))
    return i === -1 ? BLATT_TOTAL : i
  })()

  const index = phase.kind === 'sheet' ? phase.index : Math.min(firstOpen, BLATT_TOTAL - 1)
  const autosaveLabels = { saved: t('wizard.saved', { n: index + 1, total: BLATT_TOTAL }), error: t('wizard.saveError'), retry: t('wizard.retry') }
  const currentId: BlattId = BLATT_IDS[index] ?? BLATT_IDS[0]
  const current = sheets[currentId]

  useEffect(() => {
    if (phase.kind !== 'sheet') return
    if (current.daten !== undefined && current.loadedIn === language) return
    let cancelled = false
    setLoadError(false)
    const pending = recoverAutosave(currentId) ?? loaded.retained?.[currentId]
    getBlatt(currentId, sessionId)
      .then((fresh) => {
        if (cancelled) return
        updateSheets((s) => ({ ...s, [currentId]: {
          ...fresh,
          daten: fresh.status === 'unterschrieben' || s[currentId].status === 'unterschrieben'
            ? fresh.daten ?? {}
            : recoverAutosave(currentId) ?? s[currentId].daten ?? pending ?? fresh.daten ?? {},
          status: fresh.status === 'unterschrieben' ? 'unterschrieben' : s[currentId].status,
          loadedIn: language, signatureSrc: s[currentId].signatureSrc,
        } }))
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (sessionGone(error)) endedRef.current()
        else setLoadError(true)
      })
    return () => {
      cancelled = true
    }
  }, [phase.kind, currentId, current, language, sessionId, updateSheets, recoverAutosave, loaded.retained])

  const flushAutosave = autosave.flush

  const leave = useCallback(
    async (reason: WizardExit) => {
      if (exiting.current) return
      exiting.current = true
      onExit(reason, flushAutosave())
    },
    [onExit, flushAutosave],
  )
  endedRef.current = () => void leave('ended')

  useEffect(() => {
    let timer: number
    function reset() {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => void leave('idle'), IDLE_TIMEOUT_MS)
    }
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const
    reset()
    for (const evt of events) window.addEventListener(evt, reset, { passive: true })
    return () => {
      window.clearTimeout(timer)
      for (const evt of events) window.removeEventListener(evt, reset)
    }
  }, [leave])

  const step: SheetStep = view && view.id === currentId ? view.step : initialStep(currentId, current.status)
  const { state: sheetPdf, retry: retrySheetPdf } = useSheetPdf(
    phase.kind === 'sheet' && step === 'document' && SHEETS[currentId].kind === 'sign' ? currentId : null,
    current.status === 'unterschrieben',
    sessionId,
    flushAutosave,
    language,
  )
  const showStep = (next: SheetStep) => {
    setView({ id: currentId, step: next })
    scrollRef.current?.scrollTo?.({ top: 0 })
  }

  async function goTo(target: number) {
    const forward = !abgeschlossen && (phase.kind === 'welcome' || target > index)
    if (forward && phase.kind === 'sheet') {
      const sheetHere = SHEETS[currentId]
      if (sheetHere.kind !== 'sign' && !requiredFilled(sheetHere, current.daten)) {
        setBlockedBy(null)
        setFocusMissing((n) => n + 1)
        return
      }
      if (
        sheetHere.kind !== 'sign' &&
        sheetsRef.current[currentId].status === 'offen' &&
        autosave.isClean(currentId) &&
        requiredFilled(sheetHere, current.daten)
      ) {
        autosave.schedule(currentId, current.daten ?? {})
      }
      await autosave.flush()
      if (!autosave.isClean(currentId)) {
        setSaveBlocked(true)
        return
      }
    } else {
      await autosave.flush()
    }
    if (forward) {
      const blocker = BLATT_IDS.slice(0, target).find(
        (id) => !isBlattComplete(SHEETS[id], sheetsRef.current[id].status, sheets[id].daten),
      )
      if (blocker) {
        setBlockedBy(blocker)
        return
      }
    }
    setBlockedBy(null)
    setNotice(null)
    setSaveBlocked(false)
    setView(null)
    onNavigate({ kind: 'sheet', index: Math.min(Math.max(0, Math.trunc(target)), BLATT_TOTAL - 1) })
    scrollRef.current?.scrollTo?.({ top: 0 })
  }

  function onChange(daten: Daten) {
    if (abgeschlossen || current.status === 'unterschrieben') return
    updateSheets((s) => ({ ...s, [currentId]: { ...s[currentId], daten } }))
    autosave.schedule(currentId, daten)
  }

  async function openSignature() {
    if (sheetPdf?.kind !== 'ready') return
    await autosave.flush()
    if (!autosave.isClean(currentId)) {
      setSaveBlocked(true)
      return
    }
    setSaveBlocked(false)
    setNotice((n) => (n === 'signUnclear' ? null : n))
    setSigning(true)
  }

  async function retrySave() {
    if (sheetPdf) {
      setSaveBlocked(false)
      retrySheetPdf()
      return
    }
    const saved = await autosave.flush(true)
    if (saved || autosave.isClean(currentId)) setSaveBlocked(false)
  }

  async function submitSignature(dataUrl: string) {
    const id = currentId
    let result: Awaited<ReturnType<typeof signBlatt>>
    try {
      result = await signBlatt(id, pngBase64(dataUrl), sessionId)
    } catch (error) {
      if (currentPortalSession() !== sessionId) throw error
      if (error instanceof PortalTimeout) signTimedOut.current.add(id)
      if (signTimedOut.current.has(id) && sessionGone(error)) {
        setNotice('signUnclear')
        setSigning(false)
      } else if (sessionGone(error)) {
        endedRef.current()
      } else {
        const detail = portalErrorDetail(error)
        toastError(detail ? `${t('sign.error')} (${detail})` : t('sign.error'))
      }
      throw error
    }
    if (currentPortalSession() !== sessionId) return
    signTimedOut.current.delete(id)
    setView({ id, step: 'saved' })
    const signedAt = result === 'unterschrieben' ? new Date().toISOString() : null
    updateSheets((s) => ({ ...s, [id]: {
      ...s[id], status: 'unterschrieben', signatureSrc: dataUrl,
      unterschrieben_am: signedAt ?? s[id].unterschrieben_am ?? null,
    } }))
  }

  async function finish(retryFailed = false) {
    if (finishing.current || exiting.current) return
    finishing.current = true
    try {
      setClosing('busy')
      const saved = await autosave.flush(retryFailed)
      if (currentPortalSession() !== sessionId) return
      if (!saved) {
        setClosing('failed')
        toastError(t('wizard.finishError'))
        return
      }
      let result: Awaited<ReturnType<typeof abschliessen>>
      try {
        result = await abschliessen(sessionId)
      } catch (error) {
        if (currentPortalSession() !== sessionId) return
        if (error instanceof PortalTimeout) finishTimedOut.current = true
        if (finishTimedOut.current && sessionGone(error)) {
          setClosing('unclear')
        } else if (sessionGone(error)) {
          setClosing('idle')
          endedRef.current()
        } else {
          setClosing('failed')
          toastError(t('wizard.finishError'))
        }
        return
      }
      if (currentPortalSession() !== sessionId) return
      if (result.ok) {
        await leave('finished')
        return
      }
      const fehlend = result.fehlend.filter(isBlattId)
      if (fehlend.length > 0) {
        updateSheets((s) => {
          const next = { ...s }
          for (const id of fehlend) next[id] = { ...next[id], status: 'offen' }
          return next
        })
        setClosing('missing')
        toastError(t('wizard.finishMissing'))
        autoClosed.current = false
      } else {
        setClosing(finishTimedOut.current ? 'unclear' : 'failed')
        if (!finishTimedOut.current) toastError(t('wizard.finishError'))
      }
    } finally {
      finishing.current = false
    }
  }
  const finishRef = useRef(finish)
  finishRef.current = finish
  const autoClosed = useRef(false)
  useEffect(() => {
    if (abgeschlossen || !allComplete || autoClosed.current) return
    autoClosed.current = true
    void finishRef.current()
  }, [abgeschlossen, allComplete])

  async function changeLanguage(next: Language) {
    try {
      await setSprache(next, sessionId)
    } catch (error) {
      if (currentPortalSession() !== sessionId) return
      if (sessionGone(error)) {
        endedRef.current()
        return
      }
    }
    if (currentPortalSession() === sessionId) await setLanguage(next)
  }

  const steps = (
    <Stepper aria-label={t('wizard.progress')}>
      {BLATT_IDS.map((id, i) => {
        const done = complete(id)
        const isCurrent = phase.kind === 'sheet' && i === index
        const blocked = i > firstOpen && (phase.kind !== 'sheet' || i > index)
        return (
          <StepperStep
            key={id}
            isDone={done}
            isCurrent={isCurrent}
            isBlocked={blocked}
            aria-label={t(done ? 'wizard.sheetDone' : blocked ? 'wizard.sheetBlocked' : 'wizard.sheetOpen', { n: i + 1 })}
            title={t(`blatt.${blattKey(id)}`)}
            onClick={() => (blocked ? setBlockedBy(BLATT_IDS[firstOpen]) : void goTo(i))}
          >
            {done ? <Icon name="i-check" /> : i + 1}
          </StepperStep>
        )
      })}
    </Stepper>
  )
  const blockedNotice = blockedBy ? (
    <Callout tone="error" role="alert" title={t('wizard.blocked', {
      n: BLATT_IDS.indexOf(blockedBy) + 1,
      titel: readingInhalt(sheets[blockedBy], language)?.titel?.trim() || t(`blatt.${blattKey(blockedBy)}`),
    })}>
      {t('wizard.blockedMissing', {
        list: fehlendAuf(SHEETS[blockedBy], sheets[blockedBy].status, sheets[blockedBy].daten)
          .map((f) => missingLabel(f, t))
          .join(', '),
      })}
    </Callout>
  ) : null
  const closingNotice = (
    <>
      {closing === 'busy' ? (
        <Paragraph variant="muted" role="status">
          {t('wizard.closing')}
        </Paragraph>
      ) : null}
      {closing === 'failed' ? (
        <FormSection compact>
          <Callout tone="error" role="alert">
            {t('wizard.finishError')}
          </Callout>
          {!personHeader && <div>
            <Button variant="primary" size="lg" onClick={() => void finish(true)}>
              {t('wizard.retry')}
            </Button>
          </div>}
        </FormSection>
      ) : null}
      {closing === 'missing' ? (
        <Callout tone="error" role="alert">
          {t('wizard.finishMissing')}
        </Callout>
      ) : null}
      {closing === 'unclear' ? (
        <Paragraph variant="muted" role="status">
          {t('wizard.finishUnclear')}
        </Paragraph>
      ) : null}
    </>
  )
  const progress = <ProgressBar aria-hidden="true" value={doneCount} max={BLATT_TOTAL} />

  const topbar = (actions?: React.ReactNode) => (
    <ModalHead
      {...(personHeader ? {
        left: { type: 'title' as const, icon: 'i-person', label: personHeader.name, onClick: () => { if (closing !== 'busy') { void leave('later'); personHeader.onOpen() } } },
        center: <Tabs items={[
          ...(phase.kind === 'welcome' ? [{ name: 'welcome', label: t('welcome.title'), disabled: closing === 'busy' }] : []),
          ...BLATT_IDS.map((id, i) => ({ name: id, label: `${id} · ${t(`blatt.${blattKey(id)}`)}`, completed: complete(id), disabled: closing === 'busy' || (i > firstOpen && (phase.kind !== 'sheet' || i > index) && !abgeschlossen) })),
        ]} activeName={phase.kind === 'welcome' ? 'welcome' : currentId} onChange={(id) => { if (isBlattId(id)) void goTo(BLATT_IDS.indexOf(id)) }} />,
      } : { title: phase.kind === 'sheet' ? t('wizard.header', { n: index + 1, total: BLATT_TOTAL }) : t('appName') })}
      actions={
        <>
        {personHeader && closing === 'failed' ? (
          <Pill><Button primaryAction onClick={() => void finish(true)}>{t('wizard.retry', { lng: 'de' })}</Button></Pill>
        ) : actions && <Pill>{actions}</Pill>}
        {!abgeschlossen && <AutosaveStatus labels={autosaveLabels} state={autosave.status} identity={autosaveIdentity} onRetry={retrySave} />}
        <LanguageSwitcher
          value={language}
          label={t('language.switcher')}
          onChange={(next) => void changeLanguage(next)}
        />
        </>
      }
      onClose={() => void leave('later')}
      closeLabel={t('wizard.close')}
      closeDisabled={closing === 'busy'}
    />
  )

  if (phase.kind === 'welcome') {
    const n = index + 1
    const lead = allComplete
      ? closing === 'failed' || closing === 'unclear'
        ? null
        : t('welcome.allDone')
      : t('welcome.continueWith', { n, total: BLATT_TOTAL })
    return (
      <Wizard onClose={() => void leave('later')} bar={topbar(personHeader && !allComplete ? <Button primaryAction onClick={() => void goTo(index)}>Weiter<Icon name="i-arrow-right" /></Button> : undefined)} scrollRef={scrollRef}>
        <WizardPage narrow>
          <Panel>
            <Title as="h1" size="lg">
              {t('welcome.title')}
            </Title>
            {lead ? <Paragraph variant="lead">{lead}</Paragraph> : null}
            {progress}
            {steps}
            {blockedNotice}
            {closingNotice}
            {allComplete || personHeader ? null : (
              <Button variant="primary" size="lg" block onClick={() => void goTo(index)}>
                {t('welcome.button', { n })}
                <Icon name="i-arrow-right" mirrorRtl />
              </Button>
            )}
          </Panel>
        </WizardPage>
      </Wizard>
    )
  }

  const sheet = SHEETS[currentId]
  const isLast = index === BLATT_TOTAL - 1
  const signed = current.status === 'unterschrieben'
  const eintritt = sheets['04.0'].daten
  const signerName = `${reviewValue(eintritt, 'vorname')} ${reviewValue(eintritt, 'familienname')}`
    .trim()
    .toLocaleUpperCase('de')

  const back =
    index > 0 ? (
      <Button size="lg" onClick={() => void goTo(index - 1)}>
        <Icon name="i-arrow-left" mirrorRtl />
        {t('wizard.back', { lng: personHeader ? 'de' : language })}
      </Button>
    ) : null
  const nextSheet = isLast ? null : (
    <Button primaryAction={!!personHeader} variant="primary" size="lg" onClick={() => void goTo(index + 1)}>
      {t('wizard.next', { lng: personHeader ? 'de' : language })}
      <Icon name="i-arrow-right" mirrorRtl />
    </Button>
  )

  let body: React.ReactNode
  let actions: React.ReactNode
  if (current.daten === undefined) {
    body = loadError ? (
      <Panel>
        <Callout tone="error" role="alert">
          {t('wizard.loadError')}
        </Callout>
        <div>
          <Button size="lg" onClick={() => updateSheets((s) => ({ ...s, [currentId]: { ...s[currentId], loadedIn: undefined } }))}>
            {t('wizard.retry')}
          </Button>
        </div>
      </Panel>
    ) : (
      <Panel aria-busy="true">
        <Paragraph variant="muted">{t('wizard.loading')}</Paragraph>
      </Panel>
    )
    actions = back
  } else if (step === 'saved') {
    body = (
      <Panel centered>
        <Confirmation tone="success" inline title={t('wizard.saved', { n: index + 1, total: BLATT_TOTAL })} headingLevel="h2">
          {t('wizard.savedText')}
        </Confirmation>
      </Panel>
    )
    actions = isLast ? null : (
      <Button primaryAction={!!personHeader} variant="primary" size="lg" onClick={() => void goTo(index + 1)}>
        {t('wizard.nextSheet', { lng: personHeader ? 'de' : language, n: index + 2, total: BLATT_TOTAL })}
        <Icon name="i-arrow-right" mirrorRtl />
      </Button>
    )
  } else if (step === 'document' && sheet.kind === 'sign') {
    body = (
      <SheetDocument
          sheet={sheet}
          status={current.status}
          daten={current.daten}
          inhalt={current.inhalt}
          inhaltDe={current.inhalt_de}
          language={language}
          unterschriebenAm={current.unterschrieben_am}
        signatureSrc={current.signatureSrc}
        signerName={signerName}
        bindingPdf={
          sheetPdf?.kind === 'failed' ? (
            <FormSection compact>
              <Callout tone="error" role="alert">
                {t('wizard.loadError')}
              </Callout>
              <div>
                <Button size="lg" onClick={() => void retrySave()}>
                  {t('wizard.retry')}
                </Button>
              </div>
            </FormSection>
          ) : (
            <PdfRegion
              path={null}
              localBlob={sheetPdf?.kind === 'ready' ? sheetPdf.blob : null}
              waiting={sheetPdf?.kind !== 'ready'}
              loadingLabel={t('wizard.loading')}
              placeholder={t('wizard.loading')}
              inline
            />
          )
        }
      />
    )
    actions = signed || abgeschlossen ? (
      <>
        {back}
        {nextSheet}
      </>
    ) : (
      <>
        {hasQuestions(sheet) ? (
          <Button size="lg" onClick={() => showStep('form')}>
            <Icon name="i-edit" />
            {t('wizard.edit', { lng: personHeader ? 'de' : language })}
          </Button>
        ) : (
          back
        )}
        <Button primaryAction={!!personHeader} variant="primary" size="lg" disabled={sheetPdf?.kind !== 'ready'} onClick={() => void openSignature()}>
          <Icon name="i-signature" />
          {t('sign.button', { lng: personHeader ? 'de' : language })}
        </Button>
      </>
    )
  } else {
    body = (
      <SheetView
        sheet={sheet}
        status={current.status}
        daten={current.daten}
        inhalt={readingInhalt(current, language)}
        readOnly={abgeschlossen}
        focusMissing={focusMissing}
        onChange={onChange}
      />
    )
    if (sheet.kind === 'sign' && !abgeschlossen) {
      const ready = readyToSign(sheet, current.daten)
      actions = (
        <>
          {back}
          <Button primaryAction={!!personHeader} variant="primary" size="lg" onClick={() => (ready ? showStep('document') : setFocusMissing((n) => n + 1))}>
            {t('wizard.toDocument', { lng: personHeader ? 'de' : language })}
            <Icon name="i-arrow-right" mirrorRtl />
          </Button>
        </>
      )
    } else {
      actions = (
        <>
          {back}
          {nextSheet}
        </>
      )
    }
  }

  return (
    <Wizard
      onClose={() => void leave('later')}
      bar={topbar(personHeader ? actions : undefined)}
      scrollRef={scrollRef}
      overlay={
        <>
          <SignaturePad
            open={signing}
            title={readingInhalt(current, language)?.titel?.trim() || t(`blatt.${blattKey(currentId)}`)}
            hint={t('sign.hint')}
            clearLabel={t('sign.clear')}
            cancelLabel={t('wizard.close')}
            confirmLabel={t('sign.confirm')}
            successMessage={t('sign.success')}
            onSubmit={submitSignature}
            onClose={() => setSigning(false)}
          />
        </>
      }
    >
      <WizardPage>
        {progress}
        {steps}
        {blockedNotice}
        {body}

        {closingNotice}
        {notice === 'signUnclear' ? (
          <Paragraph variant="muted" role="status">
            {t('wizard.signUnclear')}
          </Paragraph>
        ) : null}
        {saveBlocked ? (
          <FormSection compact>
            <Callout tone="error" role="alert">
              {t('wizard.unsaved')}
            </Callout>
            <div>
              <Button size="lg" onClick={() => void retrySave()}>
                {t('wizard.retry')}
              </Button>
            </div>
          </FormSection>
        ) : null}
        {!personHeader && <WizardFooter>
          {actions}
        </WizardFooter>}
      </WizardPage>
    </Wizard>
  )
}

function missingLabel(f: Fehlend, t: (key: string) => string): string {
  switch (f.kind) {
    case 'field':
      return t(`field.${f.label}`)
    case 'phone':
      return t('missing.phone')
    case 'answers':
      return t('missing.answers')
    case 'signature':
      return t('missing.signature')
    case 'unsaved':
      return t('missing.unsaved')
  }
}
